import { AlertCircleIcon, ImageUpIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useFileUpload } from "@/hooks/use-file-upload";

interface ImageInputProps {
  maxSizeMB?: number;
  onFileSelect?: (file: File) => void;
  className?: string;
  showDescription?: boolean;
  defaultValue?: string;
  showFileRequirements?: boolean;
}

export default function ImageInput({
  maxSizeMB = 5,
  onFileSelect,
  className = "",
  showDescription = true,
  defaultValue,
  showFileRequirements = false,
}: ImageInputProps) {
  const { t } = useTranslation(["workspaces"]);
  const maxSize = maxSizeMB * 1024 * 1024; // 5MB default

  const [
    { files, isDragging, errors },
    { handleDragEnter, handleDragLeave, handleDragOver, handleDrop, openFileDialog, getInputProps },
  ] = useFileUpload({
    accept: "image/jpeg,image/jpg,image/png,image/gif,image/webp",
    maxSize,
  });

  const previewUrl = files[0]?.preview || defaultValue || null;

  // Notify parent when file is selected
  if (files[0]?.file && onFileSelect && files[0].file instanceof File) {
    onFileSelect(files[0].file);
  }

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div className="relative">
        {/* Drop area */}
        <div
          className="border-input hover:bg-accent/50 data-[dragging=true]:bg-accent/50 has-[input:focus]:border-ring has-[input:focus]:ring-ring/50 relative flex min-h-52 flex-col items-center justify-center overflow-hidden rounded-xl border border-dashed p-4 transition-colors has-disabled:pointer-events-none has-disabled:opacity-50 has-[img]:border-none has-[input:focus]:ring-[3px]"
          data-dragging={isDragging || undefined}
          role="button"
          onClick={openFileDialog}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <input {...getInputProps()} className="sr-only" />
          {previewUrl ? (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="relative h-48 w-48 overflow-hidden rounded-xl">
                <img
                  alt={files[0]?.file?.name ?? ""}
                  className="h-full w-full object-cover"
                  src={previewUrl}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center px-4 py-3 text-center">
              <div
                aria-hidden="true"
                className="bg-background mb-2 flex size-11 shrink-0 items-center justify-center rounded-full border"
              >
                <ImageUpIcon className="size-4 opacity-60" />
              </div>
              <p className="mb-1.5 text-sm font-medium">
                {t(
                  "workspaces:settings.workspaceInfo.logoDialog.uploadSection.imageInput.dropText",
                )}
              </p>
              <p className="text-muted-foreground text-xs">
                {t("settings.workspaceInfo.logoDialog.uploadSection.imageInput.maxSize", {
                  size: maxSizeMB,
                })}
              </p>
            </div>
          )}
        </div>
      </div>

      {errors.length > 0 && (
        <div className="text-destructive flex items-center gap-1 text-xs" role="alert">
          <AlertCircleIcon className="size-3 shrink-0" />
          <span>{errors[0]}</span>
        </div>
      )}

      {/* File Requirements */}
      {showFileRequirements && (
        <div className="text-muted-foreground text-center text-xs">
          {t("settings.workspaceInfo.logoDialog.uploadSection.fileRequirements")}
        </div>
      )}

      {showDescription && (
        <p
          aria-live="polite"
          className="text-muted-foreground mt-2 text-center text-xs"
          role="region"
        >
          {t("settings.workspaceInfo.logoDialog.uploadSection.imageInput.description")} ∙{" "}
          <a
            className="hover:text-foreground underline"
            href="https://github.com/origin-space/originui/tree/main/docs/use-file-upload.md"
          >
            API
          </a>
        </p>
      )}
    </div>
  );
}
